/**
 * Frontend image utility functions
 * Handles Google Drive URL conversion for preview and display
 */

/**
 * Generates a placeholder image as data URI (no external API calls)
 * @param {number} width - Image width in pixels
 * @param {number} height - Image height in pixels
 * @param {string} text - Text to display on placeholder
 * @returns {string} - Data URI for placeholder image
 */
export function getPlaceholderImage(width = 50, height = 50, text = 'No Image') {
  // Create a simple SVG placeholder as data URI (no external API calls)
  const fontSize = Math.max(10, Math.min(width / 5, 14));
  const svg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="#f0f0f0"/><text x="50%" y="50%" font-family="Arial, sans-serif" font-size="${fontSize}" fill="#999" text-anchor="middle" dy=".3em">${encodeURIComponent(text)}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${svg}`;
}

/**
 * Converts Google Drive share link to direct image URL for display
 * @param {string} driveUrl - Google Drive share URL
 * @returns {string} - Direct image URL
 */
export function convertGoogleDriveUrlForDisplay(driveUrl) {
  if (!driveUrl || typeof driveUrl !== 'string') {
    return driveUrl;
  }

  // Remove whitespace
  driveUrl = driveUrl.trim();

  // If it's a base64 data URL, return as is (don't process)
  if (driveUrl.startsWith('data:image/')) {
    return driveUrl;
  }

  // If it's a Cloudinary URL or other CDN URL, return as is
  if (driveUrl.includes('cloudinary.com') || driveUrl.includes('res.cloudinary.com')) {
    return driveUrl;
  }

  // Skip problematic Google URLs that are known to fail
  // lh3.googleusercontent.com URLs often fail due to authentication/permissions
  if (driveUrl.includes('lh3.googleusercontent.com')) {
    // Return placeholder instead of trying to load
    return getPlaceholderImage(300, 300, 'No Image');
  }

  // Skip Google Drive download URLs that are known to fail
  if (driveUrl.includes('drive.google.com/uc?export=download')) {
    // Try to convert to view format, but if it fails, use placeholder
    const fileIdMatch = driveUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (fileIdMatch && fileIdMatch[1]) {
      // Try view format instead
      return `https://drive.google.com/uc?export=view&id=${fileIdMatch[1]}`;
    }
    // If can't extract ID, return placeholder
    return getPlaceholderImage(300, 300, 'No Image');
  }

  // If already a direct image URL, return as is
  if (driveUrl.match(/^https?:\/\/.*\.(jpg|jpeg|png|gif|webp|svg)/i)) {
    return driveUrl;
  }

  // If already converted, return as is
  if (driveUrl.includes('uc?export=view')) {
    return driveUrl;
  }

  // Extract file ID from Google Drive URL
  let fileId = null;
  const patterns = [
    /\/file\/d\/([a-zA-Z0-9_-]+)/,  // /file/d/FILE_ID
    /[?&]id=([a-zA-Z0-9_-]+)/,      // ?id=FILE_ID or &id=FILE_ID
    /\/d\/([a-zA-Z0-9_-]+)/,        // /d/FILE_ID
  ];

  for (const pattern of patterns) {
    const match = driveUrl.match(pattern);
    if (match && match[1]) {
      fileId = match[1];
      break;
    }
  }

  if (!fileId) {
    return driveUrl; // Return original if can't extract
  }

  // Try multiple Google Drive image access methods
  // Method 1: uc?export=view (preferred for display)
  // Method 2: thumbnail (fallback, more reliable but smaller)
  // Note: Google Drive may require the file to be shared with "Anyone with the link"
  // and the file must be an image format (jpg, png, etc.)
  
  // Use uc?export=view for direct image display
  // If this doesn't work, the onError handler can try thumbnail
  return `https://drive.google.com/uc?export=view&id=${fileId}`;
}

/**
 * Converts multiple Google Drive URLs for display
 * @param {string} urlsString - Comma-separated URLs
 * @returns {string[]} - Array of converted URLs
 */
export function convertGoogleDriveUrlsForDisplay(urlsString) {
  if (!urlsString || typeof urlsString !== 'string') {
    return [];
  }

  return urlsString
    .split(',')
    .map(url => convertGoogleDriveUrlForDisplay(url.trim()))
    .filter(url => url && url.trim().length > 0);
}

